import {Component, OnInit} from '@angular/core';
import {AbstractDataProvider} from '../AbstractDataProvider';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {Config} from '../../shared/model/interfaces';
import {finalize} from 'rxjs/operators';
import {getErrorMessageText} from '../helper';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {AuthServiceCustom} from '../../shared/service/auth.service';

export interface IssuesAndDirectorsInterface {
  Directors: DirectorInterface[];
  Issues: IssueInterface[];
}

export interface IssueInterface {
  FundId: string;
  BoardVote: {
    Against: boolean;
    For: boolean;
  };
  IsDirectorsIssue: boolean;

  Issue: string;
  IssueDescription: string;
  IssueNumber: number;
  ProxyDate: string;
  Vote: {
    Against: boolean;
    For: boolean;
  };
}

export interface DirectorInterface {
  FundId: string;
  BoardVote: {
    Against: boolean;
    For: boolean;
  };
  Director: string;
  DirectorNumber: number;
  ProxyDate: string;
  Vote: {
    Against: boolean;
    For: boolean;
  };
}

@Component({
    selector: 'app-proxy',
    templateUrl: './proxy.component.html',
    styleUrls: ['./proxy.component.scss'],
    standalone: false
})
export class ProxyComponent implements OnInit {
  display = false;
  alertMessage: string;
  config: Config;
  issues: IssueInterface[];
  directors: DirectorInterface[];
  cardNum: string;
  form = new UntypedFormGroup({
    controlNumber: new UntypedFormControl(null,
      [Validators.required, Validators.minLength(11), Validators.maxLength(11), Validators.pattern('^[0-9]*$')])
  });

  allRowsVoteValue: string;

  voteOptions = [
    {value: 'For'},
    {value: 'Against'},
    {value: 'Abstain'}
  ];

  directorVoteOptions = [
    {value: 'For'},
    {value: 'Against'},
  ];

  selectedVoteValues: string[];
  selectedDirectorsVoteValues: string[];
  isMenuShown: boolean;

  constructor(
    private abstractDataProvider: AbstractDataProvider,
    private ngxService: NgxUiLoaderService,
    private authService: AuthServiceCustom
  ) {

  }

  ngOnInit() {
    this.isMenuShown = this.authService.isAuthenticated();
  }

  validate() {

    this.selectedVoteValues = null;
    this.selectedDirectorsVoteValues = null;
    this.allRowsVoteValue = null;
    this.config = null;
    this.issues = null;
    this.directors = null;
    this.cardNum = '';

    if (this.form.invalid) {
      this.form.markAsTouched();
      this.form.markAsDirty();
      return;
    }

    this.ngxService.start();
    const val = this.form.get('controlNumber').value;
    this.abstractDataProvider.postData(null, 'api/Proxy/ValidateCard?cardNumber=' + val)
      .subscribe((data: IssueInterface | { Message: string }) => {
          this.ngxService.stop();
          if ((data as { Message: string })?.Message) {
            this.display = true;
            this.alertMessage = (data as { Message: string })?.Message;
          } else {
            this.cardNum = val;
            this.loadIssuesAndDirectors();
          }
        },
        error => {
          this.ngxService.stop();
          this.display = true;
          this.alertMessage = getErrorMessageText(error);
        }
      );
  }

  private loadIssuesAndDirectors(): void {
    this.ngxService.start();
    this.abstractDataProvider.getData('api/Proxy/GetIssues?cardNumber=' + this.cardNum)
      .pipe(finalize(() => this.ngxService.stop()))
      .subscribe((data) => {
          this.issues = data.Data.Issues;
          this.directors = data.Data.Directors;
          this.selectedVoteValues = data.Data?.Issues.map(_ => null);
          this.selectedDirectorsVoteValues = data.Data?.Directors.map(_ => null);
        },
        error => {
          this.display = true;
          this.alertMessage = getErrorMessageText(error);
        }
      );
  }


  onConfirm(): void {
    this.display = false;
  }

  voteAsRecommends(): void {
    this.issues?.forEach((issue, i) => {
      const option = Object.keys(issue.BoardVote).find(op => issue.BoardVote[op]);
      this.selectedVoteValues[i] = option;
    });
    this.directors?.forEach((director, i) => {
      const option = Object.keys(director.BoardVote).find(op => director.BoardVote[op]);
      this.selectedDirectorsVoteValues[i] = option;
    });
    this.updateAllValueButton();
  }

  registerVote() {
    if (!this.selectedVoteValues.filter((_, i) => !this.issues[i].IsDirectorsIssue).every(Boolean) || !this.selectedDirectorsVoteValues.every(Boolean)) {
      this.display = true;
      this.alertMessage = 'A response is required for all questions.';
      return;
    } else {

      const copyIssues = JSON.parse(JSON.stringify(this.issues));
      const copyDirectors = JSON.parse(JSON.stringify(this.directors));

      copyIssues.forEach((item, index) => {
        const keys = Object.keys(item.Vote);
        keys.forEach(k => item.Vote[k] = this.selectedVoteValues[index] === k);
      });

      copyDirectors.forEach((item, index) => {
        const keys = Object.keys(item.Vote);
        keys.forEach(k => item.Vote[k] = this.selectedDirectorsVoteValues[index] === k);
      });

      const dto = {
        Issues: copyIssues,
        Directors: copyDirectors,
        CardNumber: this.cardNum
      };

      this.ngxService.start();
      this.abstractDataProvider.postData(dto, 'api/Proxy/SubmitVote?cardNumber=' + this.cardNum)
        .pipe(finalize(() => this.ngxService.stop()))
        .subscribe((data) => {

            if ((data as { Message: string })?.Message) {
              this.display = true;
              this.alertMessage = (data as { Message: string })?.Message;
            }
          },
          error => {
            this.display = true;
            this.alertMessage = getErrorMessageText(error);
          }
        );
    }
  }

  setAllVote(value: string) {
    this.allRowsVoteValue = value;
    this.selectedVoteValues.fill(value);

    if (value === 'Abstain') {
      this.selectedDirectorsVoteValues.fill(null);
    } else  {
      this.selectedDirectorsVoteValues.fill(value);

    }
  }

  setVote(i: number, value: string) {
    this.selectedVoteValues[i] = value;
    this.updateAllValueButton();
  }

  private updateAllValueButton() {
    this.allRowsVoteValue = this.selectedVoteValues.length > 0 && this.selectedVoteValues.every(v => v === this.selectedVoteValues[0])
      ? this.selectedVoteValues[0]
      : null;
  }
}
